import { useEffect } from 'react';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { FirestoreDB, LogAnalyticsEvent } from '../../../utils/firebase/firebase';
import { ANALYTICS_KEYS } from '../../../constants/AnalyticKeys';

const AnalyticsService = () => {
 
  const doctorListCount = async () => {
    const doctorsRef = collection(FirestoreDB, 'doctorsList');
    return onSnapshot(
      doctorsRef,
      (querySnapshot) => {
        const doctorCount = querySnapshot.docs.length;

        // Log a custom event to Firebase Analytics for successfully fetching doctor data
        LogAnalyticsEvent(ANALYTICS_KEYS.COUNT.DOCTORSCOUNT, {
          doctorCount: doctorCount, // The number of doctors fetched
          message: `${doctorCount} doctors successfully fetched`,
        });
      },
      (error) => {
        console.log('Error listening to doctorsList:', error);
        // Log a custom event to Firebase Analytics for an error during fetch
        LogAnalyticsEvent('doctor_list_fetch_error', {
          errorMessage: error.message, // Capture error message
          message: `Error fetching doctor list: ${error.message}`,
        });
      }
    );
  };

  const patientListCount = async () => {
    const patientsRef = collection(FirestoreDB, 'patientsList');
    return onSnapshot(
      patientsRef,
      (querySnapshot) => {
        const patientCount = querySnapshot.docs.length;

        // Log a custom event to Firebase Analytics for successfully fetching patient data
        LogAnalyticsEvent(ANALYTICS_KEYS.COUNT.PATIENTSCOUNT, {
          patientCount: patientCount, // The number of patients fetched
          message: `${patientCount} patients successfully fetched`,
        });
      },
      (error) => {
        console.log('Error listening to patientsList:', error);
        // Log a custom event to Firebase Analytics for an error during fetch
        LogAnalyticsEvent('patient_list_fetch_error', {
          errorMessage: error.message, // Capture error message
          message: `Error fetching patient list: ${error.message}`,
        });
      }
    );
  };

  useEffect(() => {
    const videoUploadCount = () => {
      const videosRef = collection(FirestoreDB, 'videoLogs');

      const unsubscribe = onSnapshot(
        videosRef,
        (querySnapshot) => {
          Promise.resolve()
            .then(() => {
              let totalVideoCount = 0;
              const videoCountPromises = querySnapshot.docs.map((doc) => {
                const userRawVideosRef = collection(doc.ref, 'rawVideos');
                return getDocs(userRawVideosRef).then(
                  (userRawVideosSnapshot) => {
                    totalVideoCount += userRawVideosSnapshot.size;
                  }
                );
              });

              return Promise.all(videoCountPromises).then(() => {
                LogAnalyticsEvent(ANALYTICS_KEYS.COUNT.VIDEOSCOUNT, {
                  totalVideoCount,
                  message: `Successfully fetched ${totalVideoCount} videos across all users.`,
                });
              });
            })
            .catch((error) => {
              console.error('Error fetching videos:', error);

              LogAnalyticsEvent('video_upload_fetch_error', {
                errorMessage: error.message,
                message: `Error fetching video data: ${error.message}`,
              });
            });
        },
        (error) => {
          console.error('Error listening to videoLogs:', error);
          LogAnalyticsEvent('video_upload_listener_error', {
            errorMessage: error.message,
            message: `Error listening to videoLogs: ${error.message}`,
          });
        }
      );

      return unsubscribe;
    };

    const unsubscribe = videoUploadCount();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  useEffect(() => {
    let unsubscribe;

    const documentUploadCount = async () => {
      try {
        const documentsRef = collection(FirestoreDB, 'historicalData');

        unsubscribe = onSnapshot(
          documentsRef,
          async (querySnapshot) => {
            const fetchDocumentCounts = async () => {
              const totalDocumentPromises = querySnapshot.docs.map(
                async (doc) => {
                  const userRawDocumentsRef = collection(
                    doc.ref,
                    'attachments'
                  );
                  const userRawDocumentsSnapshot = await getDocs(
                    userRawDocumentsRef
                  );
                  return userRawDocumentsSnapshot.size;
                }
              );

              const documentCounts = await Promise.all(totalDocumentPromises);
              const totalDocumentCount = documentCounts.reduce(
                (acc, count) => acc + count,
                0
              );

              LogAnalyticsEvent(ANALYTICS_KEYS.COUNT.DOCUMENTSCOUNT, {
                totalDocumentCount,
                message: `Successfully fetched ${totalDocumentCount} documents across all users.`,
              });
            };
            debounce(fetchDocumentCounts, 300)();
          },
          (error) => {
            console.error('Error listening to documentLogs:', error);
            LogAnalyticsEvent('document_upload_listener_error', {
              errorMessage: error.message,
              message: `Error listening to documentLogs: ${error.message}`,
            });
          }
        );
      } catch (error) {
        console.error('Error setting up snapshot listener:', error);
      }
    };

    documentUploadCount();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    doctorListCount();
    patientListCount();
  }, []);

  return null; // This component doesn't render anything
};

export default AnalyticsService; 